import { destinationsPath, marketplaceIndexPath, programsPath } from "@travily/routes";
import type { IconName } from "@ui/components/Icon";
import type { SubNavItemColor } from "@ui/components/SubNav";
import { isNavActive } from "@/layouts/components/bottom-tabs";
import type { MapboxFeatureType } from "@/lib/where-bounding-boxes";

/** Destinations search areas and cities, not administrative subdivisions (vs. programs' default).
 *  Shared by the destinations index page and the search overlay via the `destinations` section. */
export const DESTINATION_WHERE_TYPES = ["country", "region", "place"] as const satisfies readonly MapboxFeatureType[];

/**
 * Single source of truth for the Explore browse sections (Programs / Destinations / Marketplace).
 * Shared by the mobile bottom tab bar, the search overlay's tabs, and the search-centric top
 * bar so order, colours, icons, labels, and search kind never drift between them.
 */

// Placeholder keys are referenced dynamically via `t(section.placeholderKey)`, so the i18n-tasks
// scanner can't see them — declare their use explicitly. Label keys need no declaration: the
// desktop top bar references the same `nav.*` keys statically.
// i18n-tasks-use t('components.frames.app_layout.explore_search.placeholder_programs')
// i18n-tasks-use t('components.frames.app_layout.explore_search.placeholder_destinations')
// i18n-tasks-use t('components.frames.app_layout.explore_search.placeholder_marketplace')

export type ExploreSectionKey = "programs" | "destinations" | "marketplace";

/** How a section is searched — `where` = location autocomplete; `query` = free-text. */
export type ExploreSearchKind = "where" | "query";

export interface ExploreSection {
  key: ExploreSectionKey;
  /** Resolved index path (e.g. `/programs`). */
  path: string;
  icon: IconName;
  /** Active-underline tint — mirrors the desktop top-bar selected-state background. */
  color: SubNavItemColor;
  /** i18n key for the section label (reuses the existing top-bar nav labels). */
  labelKey: string;
  /** i18n key for the search-pill placeholder when no search is applied. */
  placeholderKey: string;
  searchKind: ExploreSearchKind;
  /** Mapbox feature types for the location autocomplete (`where` sections only). */
  whereTypes?: readonly MapboxFeatureType[];
  /** Mobile search shows the When (start/end date) sections. `where` sections only. */
  hasWhen?: boolean;
  /** Mobile search shows the Who section. `where` sections only. */
  hasWho?: boolean;
}

const PROGRAMS: ExploreSection = {
  key: "programs",
  path: programsPath(),
  icon: "location",
  color: "pink",
  labelKey: "components.frames.app_layout.nav.programs",
  placeholderKey: "components.frames.app_layout.explore_search.placeholder_programs",
  searchKind: "where",
  hasWhen: true,
  hasWho: true,
};

const DESTINATIONS: ExploreSection = {
  key: "destinations",
  path: destinationsPath(),
  icon: "map",
  color: "green",
  labelKey: "components.frames.app_layout.nav.destinations",
  placeholderKey: "components.frames.app_layout.explore_search.placeholder_destinations",
  searchKind: "where",
  whereTypes: DESTINATION_WHERE_TYPES,
  hasWhen: true,
};

const MARKETPLACE: ExploreSection = {
  key: "marketplace",
  path: marketplaceIndexPath(),
  icon: "store",
  color: "orange",
  labelKey: "components.frames.app_layout.nav.marketplace",
  placeholderKey: "components.frames.app_layout.explore_search.placeholder_marketplace",
  searchKind: "query",
};

export const EXPLORE_SECTIONS: readonly ExploreSection[] = [PROGRAMS, DESTINATIONS, MARKETPLACE];

/** Fallback when no section is active (the Explore home) — defaults to the first tab. */
export const DEFAULT_EXPLORE_SECTION = PROGRAMS;

/** The section whose index `path` matches (or is an ancestor of) `path`, else `undefined`. */
export function sectionForPath(path: string): ExploreSection | undefined {
  return EXPLORE_SECTIONS.find((section) => isNavActive(path, section.path));
}
